import React, { memo, useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { mockTasks } from '../../../mocks/mockTasks';
import {
  TaskIdCell,
  TaskTypeCell,
  ExecutionStatusCell,
  DateCell,
} from '../../../shared/components/TasksTableComponents/TasksTableComponents';
import TaskDetailsDialog from '../../../shared/dialogs/TaskDetailsDialog/TaskDetailsDialog';
import TaskStepsDialog from '../../../shared/dialogs/TaskStepsDialog/TaskStepsDialog';
import { getEdgeTasks } from '../store/actions';
import { makePersistedTextFilter, makePersistedSelectFilter } from '../../../shared/lib/tableFilters';
import { EXECUTION_STATUSES, TASK_TYPES } from '../../../shared/constants';
import SSPList from '../../../../../common/components/SSPList';
import { fileDownload } from '../../../../../common/services/fileUtils';
import { changeFilter, changePageOrSize, listClearData, setForceUpdate } from '../../../../../common/components/SSPList/actions';
import { getColumnClasses } from '../../../../../common/components/CommonTableColumns';

import '../styles/_styles.scss';

export const edgeTasksTableId = 'edgeTasksList';

export const edgeTasksDefaultSorting = { id: 'createdAt', desc: true };

const makeEdgeTasksColumns = ({
  getColumnClasses,
  textFilter,
  taskTypeFilter,
  executionStatusFilter,
  onDetailsClick,
  onStepsClick,
  TaskIdCell,
  TaskTypeCell,
  ExecutionStatusCell,
  DateCell,
}) => [
  {
    id: 'taskId',
    minWidth: 280,
    Header: 'Task ID',
    accessor: 'taskId',
    className: 'task-id-column',
    headerClassName: 'centered',
    Cell: ({ value, original }) => <TaskIdCell value={value} onDetailsClick={() => onDetailsClick(original)} />,
    filterable: true,
    Filter: textFilter,
    sortable: true,
    ...getColumnClasses({ isLink: true }),
  },
  {
    Header: 'Name',
    id: 'name',
    accessor: 'name',
    minWidth: 200,
    filterable: true,
    Filter: textFilter,
    sortable: true,
    ...getColumnClasses({}),
  },
  {
    Header: 'Type',
    id: 'type',
    accessor: 'type',
    minWidth: 180,
    Cell: ({ value }) => <TaskTypeCell type={value} />,
    filterable: true,
    Filter: taskTypeFilter,
    sortable: true,
    ...getColumnClasses({}),
  },
  {
    Header: 'Execution Status',
    id: 'executionStatus',
    accessor: 'executionStatus',
    minWidth: 140,
    Cell: ({ value, original }) => <ExecutionStatusCell status={value} onStepsClick={() => onStepsClick(original)} />,
    filterable: true,
    Filter: executionStatusFilter,
    sortable: true,
    ...getColumnClasses({}),
  },
  {
    Header: 'Created At',
    id: 'createdAt',
    accessor: 'createdAt',
    minWidth: 140,
    Cell: ({ value }) => <DateCell timestamp={value} />,
    filterable: true,
    Filter: textFilter,
    sortable: true,
    ...getColumnClasses({}),
  },
  {
    Header: 'Last Reported',
    id: 'lastReported',
    accessor: 'lastReported',
    minWidth: 140,
    Cell: ({ value }) => <DateCell timestamp={value} />,
    filterable: true,
    Filter: textFilter,
    sortable: true,
    ...getColumnClasses({}),
  },
];

const Tasks = memo(({ edgeControllerId }) => {
  const dispatch = useDispatch();
  const [taskDetailsDialogOpen, setTaskDetailsDialogOpen] = useState(false);
  const [taskStepsDialogOpen, setTaskStepsDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskForSteps, setSelectedTaskForSteps] = useState(null);

  useEffect(
    () => () => {
      dispatch(changeFilter(edgeTasksTableId, null));
      dispatch(changePageOrSize(edgeTasksTableId, 0, 20));
      dispatch(listClearData(edgeTasksTableId));
    },
    [dispatch],
  );

  useEffect(() => {
    if (edgeControllerId) {
      dispatch(setForceUpdate(edgeTasksTableId));
    }
  }, [edgeControllerId, dispatch]);

  const rows = useMemo(() => {
    const filteredTasks = edgeControllerId ? mockTasks.filter((task) => task.definition?.targetEdgeControllerId === edgeControllerId) : mockTasks;

    return filteredTasks.map((task) => ({
      uuid: task.taskId,
      ...task,
    }));
  }, [edgeControllerId]);

  const handleDetailsClick = (task) => {
    setSelectedTask(task);
    setTaskDetailsDialogOpen(true);
  };

  const handleCloseTaskDetailsDialog = () => {
    setTaskDetailsDialogOpen(false);
    setSelectedTask(null);
  };

  const handleStepsClick = (task) => {
    setSelectedTaskForSteps(task);
    setTaskStepsDialogOpen(true);
  };

  const handleCloseTaskStepsDialog = () => {
    setTaskStepsDialogOpen(false);
    setSelectedTaskForSteps(null);
  };

  const columns = useMemo(() => {
    const TextFilter = makePersistedTextFilter(500);
    const taskTypeOptions = [
      { value: '', label: 'All' },
      ...Object.values(TASK_TYPES).map((taskTypeValue) => ({ value: taskTypeValue, label: taskTypeValue })),
    ];
    const TaskTypeFilter = makePersistedSelectFilter(taskTypeOptions);
    const executionStatusOptions = [
      { value: '', label: 'All' },
      ...Object.values(EXECUTION_STATUSES).map((statusValue) => ({ value: statusValue, label: statusValue })),
    ];
    const ExecutionStatusFilter = makePersistedSelectFilter(executionStatusOptions);

    return makeEdgeTasksColumns({
      getColumnClasses,
      textFilter: TextFilter,
      taskTypeFilter: TaskTypeFilter,
      executionStatusFilter: ExecutionStatusFilter,
      onDetailsClick: handleDetailsClick,
      onStepsClick: handleStepsClick,
      TaskIdCell,
      TaskTypeCell,
      ExecutionStatusCell,
      DateCell,
    });
  }, []);

  const onGetDataAction = useMemo(() => {
    const thunkFactory = getEdgeTasks(rows);

    return (id, opts) => dispatch(thunkFactory(id, opts));
  }, [dispatch, rows]);

  const onExport = useMemo(
    () =>
      ({ items, selectAll }) => {
        const selectedIds = new Set(items || []);
        const exportRows = selectAll ? rows : rows.filter((row) => selectedIds.has(row.uuid));

        const csv = [
          ['Task ID', 'Name', 'Type', 'Execution Status', 'Created At', 'Last Reported'].join(','),
          ...exportRows.map((row) =>
            [
              row.taskId,
              row.name,
              row.type,
              row.executionStatus,
              row.createdAt || '',
              row.lastReported || ''
            ]
              .map((value) => `"${String(value ?? '').replaceAll('"', '""')}"`)
              .join(','),
          ),
        ].join('\n');

        fileDownload(csv, 'edge-tasks.csv', 'text/csv;charset=utf-8;');

        return Promise.resolve();
      },
    [rows],
  );

  if (rows.length === 0) {
    return (
      <div className="edge-tasks-empty">
        <p>{edgeControllerId ? 'No tasks found for this Edge controller.' : 'No tasks found.'}</p>
      </div>
    );
  }

  return (
    <div className="edge-tasks-table" style={{ width: '100%', height: 'calc(100dvh - 145px)' }}>
      <SSPList
        checkboxSticky
        mobileSticky
        multiselect
        exportable
        onExport={onExport}
        exportLimit={5000}
        onGetDataAction={onGetDataAction}
        defaultPageSize={20}
        defaultSorted={edgeTasksDefaultSorting}
        id={edgeTasksTableId}
        columns={columns}
        filterable
        rowsFreeHeight
        optStyles={{ height: '100%' }}
      />

      <TaskDetailsDialog open={taskDetailsDialogOpen} onClose={handleCloseTaskDetailsDialog} task={selectedTask} />

      <TaskStepsDialog
        open={taskStepsDialogOpen}
        onClose={handleCloseTaskStepsDialog}
        steps={selectedTaskForSteps?.steps}
        taskName={selectedTaskForSteps?.name}
        taskId={selectedTaskForSteps?.taskId}
      />
    </div>
  );
});

export default Tasks;
